import type { AssetExportRowDto } from '@terra/shared-contracts-assets';
import { getRoleDisplayName } from '../custom-components/application-assets/utils/role.utils';
import { downloadUrlAsFile } from './download-content.utils';

/**
 * Canonical column definitions for the platform's CSV exports.
 *
 * These are shared with the Academy lab verifiers, which check that a learner
 * uploaded a real Terra export. Keep producer and verifier on the same source
 * so a column change can't silently break a lab gate.
 */

export const ASSETS_CSV_HEADERS = [
  'ID',
  'URL',
  'Method',
  'Domain',
  'Path',
  'Status Code',
  'Created At',
  'Updated At',
  'Credential Roles',
  'Authentication Types',
] as const;

export const ASSETS_CSV_FIELD_KEYS = [
  'id',
  'url',
  'method',
  'domain',
  'path',
  'status_code',
  'created_at',
  'updated_at',
  'credential_roles',
  'authentication_types',
] as const;

export type AssetCsvRow = Record<
  (typeof ASSETS_CSV_FIELD_KEYS)[number],
  string
>;

const sanitizeCsvField = (value: unknown): string => {
  const text = String(value ?? '');
  const safeValue = /^[=+\-@]/.test(text) ? `'${text}` : text;

  return `"${safeValue.replace(/"/g, '""')}"`;
};

export const buildCsvContent = <T extends object>({
  rows,
  headers,
  fields,
}: {
  rows: readonly T[];
  headers: readonly string[];
  fields: readonly (keyof T)[];
}): string =>
  '\uFEFF' +
  [
    headers.map(sanitizeCsvField).join(','),
    ...rows.map((row) =>
      fields.map((field) => sanitizeCsvField(row[field])).join(','),
    ),
  ].join('\r\n');

export const exportToCSV = <T extends object>(
  data: readonly T[],
  headers: readonly string[],
  fields: readonly (keyof T)[],
  filename?: string,
) => {
  const csvContent = buildCsvContent({ rows: data, headers, fields });
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const defaultFilename = `export-${new Date().toISOString().split('T')[0]}.csv`;

  downloadUrlAsFile({ url, filename: filename || defaultFilename });
  URL.revokeObjectURL(url);
};

export const mapAssetExportRowToCsvRow = (
  row: AssetExportRowDto,
): AssetCsvRow => ({
  id: row.id,
  url: row.url,
  method: row.method,
  domain: row.domain,
  path: row.path,
  status_code: '',
  created_at: row.createdAt ? new Date(row.createdAt).toISOString() : '',
  updated_at: row.updatedAt ? new Date(row.updatedAt).toISOString() : '',
  credential_roles: row.credentialRoles
    .split(';')
    .map((role) => getRoleDisplayName(role.trim()))
    .filter(Boolean)
    .join('; '),
  authentication_types: '',
});
